import { z } from 'zod';

/**
 * Localization of one message (BB-55, CNT-003): the environment lists its locales with a default and fallbacks, and
 * says which profile attribute holds the language. A channel's content keeps the default fields as they are and adds
 * `localized: { [locale]: partial content }` over them, with `translationStatus` per locale. One message, one
 * statistic; the locale chosen for a send goes to its facts.
 */
export const LocaleCode = z
  .string()
  .min(2)
  .max(35)
  .transform((value, ctx) => {
    try {
      return Intl.getCanonicalLocales(value.replace(/_/g, '-'))[0]!;
    } catch {
      ctx.addIssue({ code: 'custom', message: 'not a BCP 47 language tag' });
      return z.NEVER;
    }
  });

export const EnvironmentLocale = z.strictObject({
  code: LocaleCode,
  default: z.boolean().optional(),
  /** Next locale when this one has no translation (`pt-BR` → `pt` → the default). */
  fallback: LocaleCode.optional(),
});
export type EnvironmentLocale = z.infer<typeof EnvironmentLocale>;

export const EnvironmentLocales = z
  .array(EnvironmentLocale)
  .min(1)
  .max(50)
  .refine((list) => list.filter((l) => l.default).length <= 1, 'at most one default locale')
  .refine((list) => new Set(list.map((l) => l.code)).size === list.length, 'locales must be unique');

export const TRANSLATION_STATUSES = ['missing', 'draft', 'ready'] as const;
export type TranslationStatus = (typeof TRANSLATION_STATUSES)[number];

/** `localized` and `translationStatus` of a channel's content (the rest of the content is the default locale). */
export const LocalizedParts = z.object({
  localized: z.record(LocaleCode, z.record(z.string(), z.unknown())).optional(),
  translationStatus: z.record(LocaleCode, z.enum(TRANSLATION_STATUSES)).optional(),
});

/** Locales whose text runs right to left; the renderers set the RTL flag for them. */
export const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur', 'yi', 'ps', 'sd', 'dv', 'ckb'] as const;

/** `POST …/translations.csv` answer: what was imported and the rows that could not be. */
export interface TranslationsImportResult {
  updated: number;
  errors: Array<{ row: number; message: string }>;
}
